import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeIn, staggerContainer, textVariant } from './utils/animations';
import AnimateWhenVisible from './components/AnimateWhenVisible';

const Hero = () => {
  // Add schema.org structured data for the homepage
  useEffect(() => {
    const homeSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "forca.dev - Web Development to Elevate Your Online Presence",
      "description": "Custom websites designed to boost your online presence, enhance brand visibility, and drive more conversions for your business.",
      "url": "https://forca.dev",
      "mainEntity": {
        "@type": "ProfessionalService",
        "name": "forca.dev",
        "image": "https://forca.dev/logo512.png",
        "description": "Custom websites designed to boost your online presence, enhance brand visibility, and drive more conversions for your business.",
        "url": "https://forca.dev",
        "offers": {
          "@type": "Offer",
          "description": "Custom web development services for businesses",
          "price": "Custom pricing based on project needs"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Web Development Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Landing Page Development"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "SEO Optimization"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Website Maintenance"
              }
            }
          ]
        }
      }
    };

    // Add the schema to the page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(homeSchema);
    script.id = 'hero-schema';
    
    // Remove any existing hero schema to avoid duplicates
    const existingScript = document.getElementById('hero-schema');
    if (existingScript) {
      existingScript.remove();
    }
    
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const scriptToRemove = document.getElementById('hero-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <motion.section
      variants={staggerContainer(0.2, 0.1)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      className="relative overflow-hidden py-20 sm:py-32"
      itemScope
      itemType="https://schema.org/WebPageElement"
      itemProp="mainContentOfPage"
    >
      {/* Background gradient elements */}
      <div className="absolute -left-[20%] -top-[20%] h-[500px] w-[500px] rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-3xl"></div>
      <div className="absolute -bottom-[20%] -right-[20%] h-[500px] w-[500px] rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/0 blur-3xl"></div>
      
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-between gap-12 lg:flex-row">
          {/* Hero content */}
          <motion.div 
            variants={fadeIn('right', 0.2)}
            className="max-w-3xl text-center lg:text-left"
          >
            <motion.h1 
              variants={textVariant(0.3)}
              className="text-4xl font-bold text-white sm:text-5xl md:text-6xl lg:text-7xl"
              itemProp="headline"
            >
              Elevate Your 
              <span className="block bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                Online Presence
              </span>
            </motion.h1>
            
            <motion.p 
              variants={textVariant(0.4)}
              className="mt-6 text-lg text-slate-400 sm:text-xl"
              itemProp="description"
            >
              Custom websites designed to boost your online presence, enhance brand visibility, 
              and drive more conversions for your business.
            </motion.p>
            
            <motion.div 
              variants={fadeIn('up', 0.5)}
              className="mt-10 flex flex-wrap justify-center gap-4 lg:justify-start"
            >
              <Link to="/contact" className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 p-px font-semibold text-white shadow-[0_1000px_0_0_hsl(0_0%_100%_/_0%)_inset] transition-colors hover:shadow-[0_1000px_0_0_hsl(0_0%_100%_/_2%)_inset]">
                <div className="relative rounded-xl bg-slate-950/50 px-6 py-3 transition-colors group-hover:bg-transparent">
                  <span className="relative flex items-center justify-center gap-2">
                    Get Started
                    <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">
                      <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                    </svg>
                  </span>
                </div>
              </Link>
            </motion.div>
            
            <motion.div 
              variants={fadeIn('up', 0.6)}
              className="mt-8 md:mt-10 flex flex-col items-center space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:gap-4 lg:justify-start"
            >
              <div className="flex -space-x-2 overflow-hidden">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="inline-block h-10 w-10 rounded-full border-2 border-slate-900 bg-slate-800 shadow-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5 text-slate-400" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                ))}
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-cyan-500 bg-slate-900 text-xs font-bold text-cyan-500">
                  +20
                </div>
              </div>
              <div className="text-center sm:text-left text-sm md:text-base text-slate-400">
                <span className="font-bold text-white">Worked with 20+ brands</span> on their digital journey
              </div>
            </motion.div>
          </motion.div>
          
          {/* Hero image/illustration */}
          <motion.div 
            variants={fadeIn('left', 0.3)}
            className="relative w-full max-w-xl lg:w-1/2"
          >
            <div className="relative">
              {/* Custom border gradient */}
              <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 opacity-30 blur-sm"></div>
              <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-950 to-slate-900">
                {/* Card-like image with glows */}
                <div className="aspect-[16/9] overflow-hidden">
                  <div className="absolute -left-16 -top-16 h-64 w-64 rounded-full bg-gradient-to-br from-cyan-500/20 to-transparent blur-2xl"></div>
                  <div className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-gradient-to-br from-blue-500/20 to-transparent blur-2xl"></div>
                  
                  {/* Browser mockup with code instead of image */}
                  <div className="h-full w-full bg-slate-950 p-3">
                    {/* Browser chrome */}
                    <div className="mb-2 flex items-center rounded-t-lg bg-slate-900 px-3 py-2">
                      <div className="flex space-x-1.5">
                        <div className="h-3 w-3 rounded-full bg-red-500"></div>
                        <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                      </div>
                      <div className="mx-auto flex h-6 w-2/3 items-center rounded-md bg-slate-800 px-3">
                        <div className="h-1.5 w-1.5 rounded-full bg-cyan-500"></div>
                        <div className="ml-1.5 h-1 w-24 rounded-full bg-slate-700"></div>
                      </div>
                    </div>
                    
                    {/* Code representation */}
                    <div className="grid h-[calc(100%-2rem)] grid-cols-10 gap-3">
                      {/* Side panel */}
                      <div className="col-span-3 rounded bg-slate-900 p-2">
                        <div className="mb-3 h-4 w-16 rounded bg-slate-800"></div>
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="mb-2 flex items-center space-x-2">
                            <div className={`h-3 w-3 rounded-sm ${i === 2 ? 'bg-cyan-500' : 'bg-slate-700'}`}></div>
                            <div className={`h-2 w-16 rounded ${i === 2 ? 'bg-cyan-500/50' : 'bg-slate-800'}`}></div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Main content */}
                      <div className="col-span-7 space-y-3 rounded bg-slate-900 p-3">
                        {/* Code block representation */}
                        <div className="space-y-1.5">
                          {[...Array(8)].map((_, i) => (
                            <div key={i} className="flex">
                              <div className="mr-2 w-5 text-right text-xs text-slate-700">{i+1}</div>
                              <div className="flex-1">
                                <div className={`h-2 ${
                                  i === 3 ? 'w-3/4 bg-gradient-to-r from-cyan-500/50 to-blue-500/50' : 
                                  i === 5 ? 'w-1/2 bg-gradient-to-r from-cyan-500/30 to-blue-500/30' :
                                  'w-full bg-slate-800'
                                } rounded`}></div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Preview section */}
                        <div className="mt-4 rounded bg-slate-950 p-2">
                          <div className="flex justify-between">
                            <div className="h-2 w-16 rounded bg-slate-800"></div>
                            <div className="h-2 w-8 rounded bg-cyan-500/50"></div>
                          </div>
                          <div className="mt-2 h-16 rounded bg-gradient-to-r from-slate-900 to-slate-800">
                            <div className="flex items-end justify-end h-full p-1">
                              <div className="h-4 w-16 rounded-sm bg-blue-500/30"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Feature highlights below image */}
                <div className="grid grid-cols-3 divide-x divide-slate-800 border-t border-slate-800">
                  {[
                    { label: 'Custom Design', value: '100%' },
                    { label: 'SEO Optimized', value: 'Built-in' },
                    { label: 'Conversion Rate', value: '+40%' }
                  ].map((stat, i) => (
                    <div key={i} className="p-4 text-center">
                      <div className="text-lg font-bold text-white">{stat.value}</div>
                      <div className="text-xs text-slate-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Floating badge */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ 
                duration: 0.5, 
                delay: 1.2,
                type: "spring",
                stiffness: 100
              }}
              className="absolute -right-4 top-1/4 rounded-full border border-slate-800 bg-slate-900 px-3 py-1 shadow-xl"
            >
              <div className="flex items-center gap-2">
                <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-cyan-500" aria-hidden="true">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-xs font-medium text-white">Fast Turnaround</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default Hero;
